import type { ReactElement } from 'react';
import { Box, Image } from '@zillow/constellation-10';
import {
  getSceneIllustrationUrl,
  hasDarkIllustration,
  type SceneIllustrationName,
  type SpotIllustrationTone,
} from '../SpotIllustration/digitalLibrary';

export {
  getSceneIllustrationUrl,
  SCENE_ILLUSTRATION_NAMES,
  type SceneIllustrationName,
} from '../SpotIllustration/digitalLibrary';

export const SCENE_ILLUSTRATION_WIDTH = 664;
export const SCENE_ILLUSTRATION_HEIGHT = 410;

/**
 * SceneIllustration — a Zillow Digital Library scene illustration (664 × 410).
 *
 * Scenes are the wide illustrations for heroes, waitlists and page-level empty states. The art comes
 * from the Digital Library like `SpotIllustration`, and light and dark swap the same way: both files
 * render lazily and Panda's `_dark` condition shows one (names in `LIGHT_ONLY_ILLUSTRATION_NAMES` show the
 * light file in both modes). It fills its container up to 664px wide and
 * keeps the 664 / 410 aspect ratio as it shrinks; it never grows past 664px. Pass `tone` only to force
 * one file. Always decorative: `alt=""` and `aria-hidden`.
 *
 * **DO NOT**
 * - Host a copy of Digital Library art in the app, under any file name
 * - Stretch it past 664px or change its aspect ratio
 * - Use a scene where a spot belongs (cards, inline empty states)
 */
export type SceneIllustrationProps = {
  /** Digital Library asset name, e.g. `applying-home` for `applying-home-light_svg_Original.svg`. */
  name: SceneIllustrationName;
  /** Forces one file. Omit it to follow the page's light/dark mode. */
  tone?: SpotIllustrationTone;
};

export function SceneIllustration({ name, tone }: SceneIllustrationProps): ReactElement {
  const image = (fileTone: SpotIllustrationTone, loading?: 'lazy') => (
    <Image
      src={getSceneIllustrationUrl(name, fileTone)}
      alt=""
      aria-hidden="true"
      width={SCENE_ILLUSTRATION_WIDTH}
      height={SCENE_ILLUSTRATION_HEIGHT}
      loading={loading}
      css={{ width: '100%', height: 'auto', aspectRatio: '664 / 410' }}
      data-testid="SceneIllustration"
    />
  );

  if (tone || !hasDarkIllustration(name)) {
    return <Box css={{ width: '100%', maxWidth: '664px' }}>{image(tone ?? 'light')}</Box>;
  }

  return (
    <>
      <Box css={{ display: 'block', width: '100%', maxWidth: '664px', _dark: { display: 'none' } }}>
        {image('light', 'lazy')}
      </Box>
      <Box css={{ display: 'none', width: '100%', maxWidth: '664px', _dark: { display: 'block' } }}>
        {image('dark', 'lazy')}
      </Box>
    </>
  );
}
